import { cloneDeep, isEqual } from 'lodash'
import { isRecord } from '@/utils/typeGuards'

export type LocalRowTransition<Row> = {
  before: Row | undefined
  submitted: Row
  pending: Map<symbol, Row>
  settled: Row | undefined
  staleControlled: Row | undefined
  isUnconfirmedAddition: boolean
}

const hasSameRowValues = <Row>(previous: Row, next: Row): boolean => {
  if (!isRecord(previous) || !isRecord(next)) return isEqual(previous, next)
  const previousValues = { ...previous }
  const nextValues = { ...next }
  delete previousValues.updatedAt
  delete previousValues.version
  delete nextValues.updatedAt
  delete nextValues.version
  return isEqual(previousValues, nextValues)
}

export const createLocalRowTransition = <Row>(
  before: Row | undefined,
  submitted: Row,
): LocalRowTransition<Row> => ({
  before,
  submitted: cloneDeep(submitted),
  pending: new Map(),
  settled: undefined,
  staleControlled: undefined,
  isUnconfirmedAddition: before === undefined,
})

export const recordLocalRowOperation = <Row>(
  transition: LocalRowTransition<Row>,
  token: symbol,
  submitted: Row,
) => {
  if (!isEqual(transition.submitted, submitted)) {
    transition.submitted = cloneDeep(submitted)
  }
  transition.pending.set(token, transition.submitted)
}

export const rememberControlledRow = <Row>(
  transition: LocalRowTransition<Row>,
  controlled: Row,
) => {
  if (hasSameRowValues(transition.submitted, controlled)) return
  // Keep one actually observed intermediate acknowledgement. The original
  // controlled baseline already has its own exact comparison below.
  const earlierIntent =
    transition.settled && hasSameRowValues(transition.settled, controlled)
      ? transition.settled
      : Array.from(transition.pending.values()).find((submitted) =>
          hasSameRowValues(submitted, controlled),
        )
  if (earlierIntent && !isEqual(transition.staleControlled, controlled)) {
    transition.staleControlled = cloneDeep(controlled)
  }
}

export const shouldDeferLocalRow = <Row>(
  transition: LocalRowTransition<Row>,
  controlled: Row,
  live: Row,
) => {
  if (isEqual(live, controlled)) return false
  if (transition.pending.size > 0) return true
  if (hasSameRowValues(transition.submitted, controlled)) return false
  return (
    isEqual(transition.before, controlled) ||
    (transition.staleControlled !== undefined &&
      isEqual(transition.staleControlled, controlled))
  )
}

export const settleLocalRowOperation = <Row>(
  transition: LocalRowTransition<Row>,
  token: symbol,
  live: Row,
  controlled: Row | undefined,
  rejected: boolean,
) => {
  const submitted = transition.pending.get(token)
  if (submitted === undefined) return false
  // The feature has already restored any rejected fields. Only retain that
  // current result; core never reconstructs a whole-row rollback.
  if (!isEqual(transition.submitted, live)) {
    transition.submitted = cloneDeep(live)
  }
  if (controlled !== undefined) rememberControlledRow(transition, controlled)
  transition.pending.delete(token)
  if (!rejected) {
    transition.isUnconfirmedAddition = false
    transition.settled =
      transition.pending.size === 0 ? transition.submitted : submitted
  }
  // Completed operations leave no history list. The fixed settled state and
  // outstanding snapshots bound retention by rows and work still in flight.
  return true
}
